import { z } from 'zod/v4';

export const ExportQuestionnaireParamsSchema = z.object({
  id: z.string().min(1),
});

export type ExportQuestionnaireParams = z.infer<
  typeof ExportQuestionnaireParamsSchema
>;

/**
 * Body of the export endpoint.
 *
 * Both fields are optional, matching the legacy `exportRequests` JSON Schema,
 * which declared no `required` array — so `{}` is schema-valid and is rejected a
 * layer later by the service with `no_request_selected` (400). Keeping the
 * rejection in the service rather than the schema preserves that error code;
 * a `.refine()` here would surface it as a generic validation failure instead.
 *
 * `allRequests: true` makes the server export every live answer request on the
 * questionnaire and **ignore** `requestIds` entirely — legacy behaviour, and
 * frontend-v2 leans on it by sending `requestIds: []` in that case.
 *
 * Left `.strip()` (the Zod default) rather than `.strict()`: the legacy schema's
 * `additionalProperties: false` made AJV *drop* unknown keys silently, so a
 * payload carrying extras returns 200 today. `.strict()` would newly 400 on it.
 *
 * One deliberate divergence from legacy: AJV's `coerceTypes` wrapped a non-array
 * `requestIds` into a single-element array, so `{requestIds: 'ABC'}` returned 200
 * and stored `['ABC']`. Zod does not coerce and this schema does not restore it —
 * neither frontend can emit a non-array (frontend-v2's parameter is typed
 * `string[]`, the old frontend passes an array literal), so it was incidental
 * validator behaviour rather than contract.
 */
export const ExportQuestionnaireBodySchema = z.object({
  allRequests: z.boolean().optional(),
  requestIds: z.array(z.string().min(1)).optional(),
});

export type ExportQuestionnaireBody = z.infer<
  typeof ExportQuestionnaireBodySchema
>;
